import React, { useEffect, useMemo, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Col,
  Row,
  Input,
  Label,
  Form,
  Card,
  CardBody,
  Container,
  CardHeader,
  CardFooter,
  FormFeedback,
  InputGroup,
  Button,
  Alert,
} from "reactstrap";
import { useFormik } from "formik";
import schema from "./schema";

import { BackButton } from "components/common/Buttons";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { toast } from "react-toastify";
import { PaymentSourceType } from "features/Payments/constants/payment.constants";
import {
  getInvoice,
  getInvoiceOptions,
} from "features/Invoices/services/InvoiceService";
import { createPayment } from "features/Payments/services/PaymentService";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import { getModeOfPaymentOptions } from "features/Meta/services/MetaService";
import { Invoice } from "features/Invoices/types/invoice.types";
import {
  enrichInvoiceSelectOptionData,
  InvoiceOption,
  InvoiceSingleValue,
} from "features/Invoices/components/InvoiceSelectComponents";
import { formatCurrency } from "utils/currency";
import { getDocumentTitle } from "utils/helpers";
import { getClientAdvanceOptions } from "features/ClientAdvances/services/ClientAdvanceService";
import {
  ClientAdvanceOption,
  ClientAdvanceSingleValue,
} from "features/ClientAdvances/components/ClientAdvanceSelectComponents";
import { ClientAdvanceStatus } from "features/ClientAdvances/constants/client-advance.constants";
import { getEmployeeAdvanceOptions } from "features/EmployeeAdvances/services/EmployeeAdvanceService";
import {
  EmployeeAdvanceOption,
  EmployeeAdvanceSingleValue,
} from "features/EmployeeAdvances/components/EmployeeAdvanceSelectComponents";
import { EmployeeAdvanceStatus } from "features/EmployeeAdvances/constants/employee-advance.constants";
import { getPayrollItemOptions } from "features/Payroll/services/PayrollService";
import {
  PayrollItemOption,
  PayrollItemSingleValue,
} from "features/Payroll/components/PayrollItemSelectComponents";

/** Zero-net items have no Payment to create — filter them out so the picker never offers one. */
async function getPayablePayrollItemOptions(params: {
  query: string;
  page: number;
  limit: number;
}) {
  const res = await getPayrollItemOptions({ ...params, unpaid: true });
  if (res.success && res.data?.rows) {
    return {
      ...res,
      data: {
        ...res.data,
        rows: res.data.rows.filter(
          (row: any) => Number(row.netAmount ?? 0) > 0,
        ),
      },
    };
  }
  return res;
}

const SOURCE_TYPES_FROM_QUERY = new Set<string>([
  PaymentSourceType.INVOICE,
  PaymentSourceType.CLIENT_ADVANCE,
  PaymentSourceType.EMPLOYEE_ADVANCE,
  PaymentSourceType.PAYROLL_ITEM,
]);

const PaymentForm = () => {
  document.title = getDocumentTitle("New payment");

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const querySourceType = searchParams.get("sourceType");
  const querySourceId = searchParams.get("sourceId");
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [selectedAdvance, setSelectedAdvance] = useState<{
    remainingAmount?: number;
    amount?: number;
  } | null>(null);

  const invoiceSelectComponents = useMemo(
    () => ({
      Option: InvoiceOption,
      SingleValue: (p: any) => (
        <InvoiceSingleValue
          {...p}
          data={enrichInvoiceSelectOptionData(p.data, invoice)}
        />
      ),
    }),
    [invoice],
  );

  const clientAdvanceSelectComponents = useMemo(
    () => ({ Option: ClientAdvanceOption, SingleValue: ClientAdvanceSingleValue }),
    [],
  );

  const employeeAdvanceSelectComponents = useMemo(
    () => ({ Option: EmployeeAdvanceOption, SingleValue: EmployeeAdvanceSingleValue }),
    [],
  );

  const payrollItemSelectComponents = useMemo(
    () => ({ Option: PayrollItemOption, SingleValue: PayrollItemSingleValue }),
    [],
  );

  const clientAdvanceRequestParams = {
    status: ClientAdvanceStatus.DRAFT,
    withCancelledDisbursement: true,
  };

  const employeeAdvanceRequestParams = {
    status: EmployeeAdvanceStatus.PROCESSED,
    withCancelledDisbursement: true,
  };

  const handleSourceTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    validation.setFieldValue("sourceType", value);
    validation.setFieldValue("sourceId", "");
    setInvoice(null);
    setSelectedAdvance(null);
    validation.setFieldValue("amount", "");
  };

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      referenceNumber: "",
      paymentDate: "",
      sourceType:
        querySourceType && SOURCE_TYPES_FROM_QUERY.has(querySourceType)
          ? (querySourceType as PaymentSourceType)
          : PaymentSourceType.INVOICE,
      sourceId: querySourceId ?? "",
      amount: "",
      remarks: "",
      modeOfPaymentId: "",
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      const amount = Number(values.amount);
      if (!Number.isFinite(amount) || amount <= 0) {
        toast.error("Enter a valid amount.");
        return;
      }
      const newPayment = {
        ...values,
        amount,
        modeOfPaymentId: Number(values.modeOfPaymentId),
        sourceType: values.sourceType as PaymentSourceType,
        sourceId: Number(values.sourceId),
      };

      try {
        const response = await createPayment(newPayment);
        if (response.success) {
          toast.success(response.message);
          navigate(-1);
        }
      } catch (err) {
        toast.error((err as any).response.data.message);
      }
    },
  });

  useEffect(() => {
    const fetchRecord = async () => {
      if (
        !validation.values.sourceId ||
        validation.values.sourceType !== PaymentSourceType.INVOICE
      ) {
        setInvoice(null);
        return;
      }
      try {
        const response = await getInvoice(Number(validation.values.sourceId));
        const { success, data } = response;
        if (success && data) {
          setInvoice(data);
        } else {
          setInvoice(null);
        }
      } catch (err) {
        console.warn(err);
        setInvoice(null);
      }
    };
    void fetchRecord();
  }, [validation.values.sourceId, validation.values.sourceType]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="New payment"
            pageTitle="Finance"
            items={[{ label: "Payments", path: "/finance/payments" }]}
          />
          <Form
            className="needs-validation"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <Row className="justify-content-center">
              <Col>
                <Card className="shadow-none border">
                  <CardHeader className="border-bottom bg-transparent pb-3">
                    <h5 className="card-title mb-1">Record payment</h5>
                    <p className="text-muted mb-0 small">
                      Link this payment to an invoice or advance, enter the
                      amount and how it was received, then add an optional
                      reference and notes.
                    </p>
                  </CardHeader>
                  <CardBody className="pt-4">
                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                      Source
                    </div>
                    <Row className="g-3 mb-2">
                      <Col xs={12}>
                        <Label className="form-label d-block mb-2">
                          Source type
                        </Label>
                        <div className="d-flex gap-3 flex-wrap">
                          {[
                            {
                              value: PaymentSourceType.INVOICE,
                              label: "Invoice",
                            },
                            {
                              value: PaymentSourceType.CLIENT_ADVANCE,
                              label: "Client Advance",
                            },
                            {
                              value: PaymentSourceType.EMPLOYEE_ADVANCE,
                              label: "Employee Advance",
                            },
                            {
                              value: PaymentSourceType.PAYROLL_ITEM,
                              label: "Payroll Salary",
                            },
                          ].map(({ value, label }) => (
                            <div className="form-check" key={value}>
                              <Input
                                className="form-check-input"
                                type="radio"
                                name="sourceType"
                                id={`source-type-${value}`}
                                value={value}
                                checked={validation.values.sourceType === value}
                                onChange={handleSourceTypeChange}
                              />
                              <Label
                                className="form-check-label"
                                for={`source-type-${value}`}
                              >
                                {label}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </Col>
                      <Col md={6}>
                        <Label className="form-label">
                          {validation.values.sourceType === PaymentSourceType.CLIENT_ADVANCE
                            ? "Client Advance"
                            : validation.values.sourceType === PaymentSourceType.EMPLOYEE_ADVANCE
                              ? "Employee Advance"
                              : validation.values.sourceType === PaymentSourceType.PAYROLL_ITEM
                                ? "Payroll Item"
                                : "Invoice"}
                        </Label>
                        {validation.values.sourceType === PaymentSourceType.INVOICE ? (
                          <>
                            <AsyncPaginatedSelect
                              components={invoiceSelectComponents}
                              fetchOptions={getInvoiceOptions}
                              placeholder="Search invoices…"
                              value={validation.values.sourceId}
                              onChange={(val: any) =>
                                validation.setFieldValue("sourceId", val?.value)
                              }
                              onBlur={() =>
                                validation.setFieldTouched("sourceId", true)
                              }
                              isInvalid={
                                validation.touched.sourceId &&
                                !!validation.errors.sourceId
                              }
                            />
                            {validation.errors.sourceId &&
                              (validation.touched.sourceId ||
                                validation.submitCount > 0) && (
                                <FormFeedback
                                  type="invalid"
                                  className="d-block"
                                >
                                  {validation.errors.sourceId}
                                </FormFeedback>
                              )}
                          </>
                        ) : validation.values.sourceType === PaymentSourceType.CLIENT_ADVANCE ? (
                          <>
                            <AsyncPaginatedSelect
                              fetchOptions={getClientAdvanceOptions}
                              components={clientAdvanceSelectComponents}
                              placeholder="Search client advances…"
                              requestParams={clientAdvanceRequestParams}
                              value={validation.values.sourceId}
                              onChange={(val: any) => {
                                validation.setFieldValue("sourceId", val?.value ?? null);
                                if (val) {
                                  setSelectedAdvance({ remainingAmount: val.remainingAmount, amount: val.amount });
                                  validation.setFieldValue("amount", val.amount ?? "");
                                } else {
                                  setSelectedAdvance(null);
                                  validation.setFieldValue("amount", "");
                                }
                              }}
                              onBlur={() =>
                                validation.setFieldTouched("sourceId", true)
                              }
                              isInvalid={
                                validation.touched.sourceId &&
                                !!validation.errors.sourceId
                              }
                            />
                            <p className="text-muted small mb-0 mt-1">
                              The amount is derived from the selected advance.
                            </p>
                            {validation.errors.sourceId &&
                              (validation.touched.sourceId ||
                                validation.submitCount > 0) && (
                                <FormFeedback
                                  type="invalid"
                                  className="d-block"
                                >
                                  {validation.errors.sourceId}
                                </FormFeedback>
                              )}
                          </>
                        ) : validation.values.sourceType === PaymentSourceType.EMPLOYEE_ADVANCE ? (
                          <>
                            <AsyncPaginatedSelect
                              fetchOptions={getEmployeeAdvanceOptions}
                              components={employeeAdvanceSelectComponents}
                              placeholder="Search employee advances…"
                              requestParams={employeeAdvanceRequestParams}
                              value={validation.values.sourceId}
                              onChange={(val: any) => {
                                validation.setFieldValue("sourceId", val?.value ?? null);
                                if (val) {
                                  setSelectedAdvance({ remainingAmount: val.remainingAmount, amount: val.amount });
                                  validation.setFieldValue("amount", val.amount ?? "");
                                } else {
                                  setSelectedAdvance(null);
                                  validation.setFieldValue("amount", "");
                                }
                              }}
                              onBlur={() =>
                                validation.setFieldTouched("sourceId", true)
                              }
                              isInvalid={
                                validation.touched.sourceId &&
                                !!validation.errors.sourceId
                              }
                            />
                            <p className="text-muted small mb-0 mt-1">
                              The amount is derived from the selected advance.
                            </p>
                            {validation.errors.sourceId &&
                              (validation.touched.sourceId ||
                                validation.submitCount > 0) && (
                                <FormFeedback
                                  type="invalid"
                                  className="d-block"
                                >
                                  {validation.errors.sourceId}
                                </FormFeedback>
                              )}
                          </>
                        ) : (
                          <>
                            <AsyncPaginatedSelect
                              fetchOptions={getPayablePayrollItemOptions}
                              components={payrollItemSelectComponents}
                              placeholder="Search payroll items…"
                              value={validation.values.sourceId}
                              onChange={(val: any) => {
                                validation.setFieldValue("sourceId", val?.value ?? null);
                                if (val) {
                                  setSelectedAdvance(null);
                                  validation.setFieldValue("amount", val.netAmount ?? "");
                                } else {
                                  validation.setFieldValue("amount", "");
                                }
                              }}
                              onBlur={() =>
                                validation.setFieldTouched("sourceId", true)
                              }
                              isInvalid={
                                validation.touched.sourceId &&
                                !!validation.errors.sourceId
                              }
                            />
                            <p className="text-muted small mb-0 mt-1">
                              Only unpaid items with a net amount to disburse are shown
                              (₹0-net items are fully settled by deductions/advances and
                              never need a payment). The amount is derived from the
                              item's net pay.
                            </p>
                            {validation.errors.sourceId &&
                              (validation.touched.sourceId ||
                                validation.submitCount > 0) && (
                                <FormFeedback
                                  type="invalid"
                                  className="d-block"
                                >
                                  {validation.errors.sourceId}
                                </FormFeedback>
                              )}
                          </>
                        )}
                      </Col>
                    </Row>

                    {invoice ? (
                      <Alert
                        color="light"
                        className="border mb-0 py-2"
                        role="status"
                      >
                        <span className="text-muted me-2">
                          Invoice outstanding:
                        </span>
                        <span className="fw-semibold text-body">
                          {formatCurrency(invoice.outstandingAmount)}
                        </span>
                      </Alert>
                    ) : selectedAdvance?.remainingAmount != null ? (
                      <Alert
                        color="light"
                        className="border mb-0 py-2"
                        role="status"
                      >
                        <span className="text-muted me-2">
                          Advance remaining:
                        </span>
                        <span className="fw-semibold text-body">
                          {formatCurrency(selectedAdvance.remainingAmount)}
                        </span>
                      </Alert>
                    ) : null}

                    <hr className="my-4 text-muted opacity-25" />

                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                      Payment details
                    </div>
                    <Row className="g-3 mb-2">
                      <Col md={6} lg={5}>
                        <Label className="form-label" for="payment-amount">
                          Amount (INR)
                        </Label>
                        <InputGroup>
                          <span className="input-group-text">₹</span>
                          <Input
                            id="payment-amount"
                            type="number"
                            className="form-control"
                            placeholder="0.00"
                            name="amount"
                            step="0.01"
                            value={validation.values.amount}
                            readOnly={
                              validation.values.sourceType === PaymentSourceType.CLIENT_ADVANCE ||
                              validation.values.sourceType === PaymentSourceType.EMPLOYEE_ADVANCE ||
                              validation.values.sourceType === PaymentSourceType.PAYROLL_ITEM
                            }
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={Boolean(
                              validation.touched.amount &&
                              validation.errors.amount,
                            )}
                          />
                        </InputGroup>
                        {validation.touched.amount &&
                        validation.errors.amount ? (
                          <FormFeedback type="invalid" className="d-block">
                            {validation.errors.amount}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col md={6} lg={7}>
                        <Label className="form-label">Mode of payment</Label>
                        <AsyncPaginatedSelect
                          fetchOptions={getModeOfPaymentOptions}
                          placeholder="Search modes…"
                          value={validation.values.modeOfPaymentId}
                          onChange={(val: any) => {
                            validation.setFieldValue(
                              "modeOfPaymentId",
                              val?.value,
                            );
                          }}
                          onBlur={() =>
                            validation.setFieldTouched("modeOfPaymentId", true)
                          }
                          isInvalid={
                            validation.touched.modeOfPaymentId &&
                            !!validation.errors.modeOfPaymentId
                          }
                        />
                        {validation.touched.modeOfPaymentId &&
                        validation.errors.modeOfPaymentId ? (
                          <FormFeedback type="invalid" className="d-block">
                            {validation.errors.modeOfPaymentId}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col md={6}>
                        <Label className="form-label" for="payment-date">
                          Payment date
                        </Label>
                        <Flatpickr
                          id="payment-date"
                          name="paymentDate"
                          className={`form-control ${
                            validation.errors.paymentDate &&
                            validation.touched.paymentDate
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="Select date"
                          options={{
                            altInput: true,
                            altFormat: "d M, Y",
                            dateFormat: "d M, Y",
                          }}
                          onChange={(date: any) =>
                            validation.setFieldValue(
                              "paymentDate",
                              moment(date[0]).format("DD MMMM ,YYYY"),
                            )
                          }
                          value={
                            validation.values.paymentDate
                              ? new Date(validation.values.paymentDate)
                              : ""
                          }
                        />
                        {validation.touched.paymentDate &&
                        validation.errors.paymentDate ? (
                          <FormFeedback type="invalid" className="d-block">
                            {validation.errors.paymentDate}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col md={6}>
                        <Label className="form-label" for="payment-reference">
                          Reference number{" "}
                          <span className="text-muted fw-normal">
                            (optional)
                          </span>
                        </Label>
                        <Input
                          id="payment-reference"
                          type="text"
                          className="form-control"
                          placeholder="UTR, cheque no., transaction id…"
                          name="referenceNumber"
                          value={validation.values.referenceNumber}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={Boolean(
                            validation.touched.referenceNumber &&
                            validation.errors.referenceNumber,
                          )}
                        />
                        {validation.touched.referenceNumber &&
                        validation.errors.referenceNumber ? (
                          <FormFeedback type="invalid">
                            {validation.errors.referenceNumber}
                          </FormFeedback>
                        ) : null}
                      </Col>
                    </Row>

                    <hr className="my-4 text-muted opacity-25" />

                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                      Remarks
                    </div>
                    <Row>
                      <Col>
                        <Label
                          className="form-label visually-hidden"
                          for="payment-remarks"
                        >
                          Remarks
                        </Label>
                        <Input
                          id="payment-remarks"
                          type="textarea"
                          className="form-control"
                          rows={3}
                          placeholder="Optional notes for finance or audit…"
                          name="remarks"
                          value={validation.values.remarks}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={Boolean(
                            validation.touched.remarks &&
                            validation.errors.remarks,
                          )}
                        />
                        {validation.touched.remarks &&
                        validation.errors.remarks ? (
                          <FormFeedback type="invalid">
                            {validation.errors.remarks}
                          </FormFeedback>
                        ) : null}
                      </Col>
                    </Row>
                  </CardBody>
                  <CardFooter className="bg-light bg-opacity-50 border-top d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
                    <BackButton />
                    <Button
                      type="submit"
                      color="primary"
                      className="btn-label px-4"
                      disabled={validation.isSubmitting}
                    >
                      {validation.isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                          Submitting…
                        </>
                      ) : (
                        "Record payment"
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PaymentForm;
